import { io } from 'socket.io-client';
import store from '../store';
import { Message } from 'element-ui';
import { SOCKET_CONFIG, NOTIFICATION, DEBOUNCE_DELAY } from '../constants/config.js';

class SocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = SOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS;
    this.isConnected = false;

    // 防抖定时器
    this.orderRefreshTimer = null;
    this.taskRefreshTimer = null;
    this.batchRefreshDelay = DEBOUNCE_DELAY.REFRESH;

    // 新通知回调列表
    this.newNotificationCallbacks = [];
  }

  connect() {
    const token = store.getters.token;
    if (!token) {
      console.log('[WebSocket] 未找到 token，跳过连接');
      return;
    }

    if (this.socket?.connected) {
      console.log('[WebSocket] 已连接，跳过重复连接');
      return;
    }

    console.log('[WebSocket] 开始连接...');
    const socketUrl = process.env.VUE_APP_API_URL || window.location.origin;
    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: SOCKET_CONFIG.RECONNECT_DELAY
    });

    this.setupEventListeners();
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('[WebSocket] ✅ 连接成功，socketId:', this.socket.id);
      this.reconnectAttempts = 0;
      this.isConnected = true;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] ❌ 连接断开，原因:', reason);
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] ❌ 连接错误:', error.message);
      this.reconnectAttempts++;

      // 如果认证失败（token 无效或过期），自动清除 token 并跳转登录页
      if (error.message && error.message.includes('认证失败')) {
        console.warn('[WebSocket] ⚠️ 认证失败，清除登录状态并跳转登录页');
        // 通过 Vuex store 清除认证状态（兼容 vuex-persistedstate）
        store.dispatch('logout').finally(() => {
          // 避免重复跳转
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        });
        return;
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
        this.disconnect();
      }
    });

    // 订单创建事件
    this.socket.on('order_created', (data) => {
      this.handleOrderCreated(data);
    });

    // 订单更新事件
    this.socket.on('order_update', (data) => {
      this.handleOrderUpdate(data);
    });

    // 订单分配给当前用户
    this.socket.on('order_assigned_to_you', (data) => {
      this.handleOrderAssigned(data);
    });

    // 任务被抢单
    this.socket.on('task_claimed', (data) => {
      this.handleTaskClaimed(data);
    });

    // 批量订单更新
    this.socket.on('orders_batch_updated', (data) => {
      this.handleBatchUpdate(data);
    });

    // 通知消息 - 更新未读计数
    this.socket.on('notification', (data) => {
      this.handleNotification(data);
      store.commit('INCREMENT_UNREAD_NOTIFICATIONS');
    });

    // 在线用户列表（管理员）
    this.socket.on('online_users', (data) => {
      store.commit('SET_ONLINE_USERS', data);
    });

    // 用户上线/离线通知
    this.socket.on('user_offline', (data) => {
      store.commit('REMOVE_ONLINE_USER', data.userId);
      if (store.getters.isAdmin) {
        this.showNotification('用户离线', `${data.username} 已离线`, 'info');
      }
    });

    // 用户正在输入
    this.socket.on('user_typing', (data) => {
      store.commit('SET_USER_TYPING', data);
    });

    // 结账冻结状态变更
    this.socket.on('reconciliation_frozen', (data) => {
      console.log('[WebSocket] 📢 收到冻结状态广播:', data);
      store.commit('SET_RECONCILIATION_FROZEN', data.frozen);
      if (data.frozen) {
        this.showNotification('系统提示', '系统已进入结账冻结期，已投数已锁定', 'warning');
      } else {
        this.showNotification('系统提示', '结账冻结期已结束，已投数可正常修改', 'success');
      }
    });

    // 核算期状态变更
    this.socket.on('accounting_period_changed', (data) => {
      console.log('[WebSocket] 📢 收到核算期状态广播:', data);
      store.commit('SET_ACCOUNTING_PERIOD', {
        period: data.period
      });
      
      if (data.period) {
        this.showNotification('核算期开启', data.message, 'success');
      } else {
        this.showNotification('核算期关闭', data.message, 'info');
      }
    });
  }

  // 防抖触发订单列表刷新
  triggerOrderRefresh() {
    if (this.orderRefreshTimer) {
      clearTimeout(this.orderRefreshTimer);
    }
    this.orderRefreshTimer = setTimeout(() => {
      store.commit('TRIGGER_ORDER_REFRESH');
      this.orderRefreshTimer = null;
    }, this.batchRefreshDelay);
  }

  // 防抖触发任务列表刷新
  triggerTaskRefresh() {
    if (this.taskRefreshTimer) {
      clearTimeout(this.taskRefreshTimer);
    }
    this.taskRefreshTimer = setTimeout(() => {
      store.commit('TRIGGER_TASK_REFRESH');
      this.taskRefreshTimer = null;
    }, this.batchRefreshDelay);
  }

  // 处理订单创建
  handleOrderCreated(data) {
    const userRole = store.getters.userRole;

    // 播放提示音
    this.playNotificationSound();

    if (userRole === 'admin') {
      // 管理员收到新订单通知
      this.showNotification(
        '新订单创建',
        `订单 ${data.order.order_no} 已创建`,
        'success'
      );

      // 触发刷新订单列表（防抖）
      this.triggerOrderRefresh();
    } else if (data.order.agent_id === store.getters.userId) {
      // 代投员收到新分配订单
      this.showNotification(
        '新订单分配',
        `订单 ${data.order.order_no} 已分配给您`,
        'success'
      );

      // 如果是当前在任务页面，自动刷新（防抖）
      this.triggerTaskRefresh();
    }
  }

  // 处理订单更新
  handleOrderUpdate(data) {
    const userRole = store.getters.userRole;
    const userId = store.getters.userId;

    this.playNotificationSound();

    // 如果订单属于当前代投员
    if (data.order.agent_id === userId) {
      this.showNotification(
        '订单更新',
        `订单 ${data.order.order_no} 已更新`,
        'info'
      );
      this.triggerTaskRefresh();
    }

    // 管理员始终收到更新
    if (userRole === 'admin') {
      this.triggerOrderRefresh();
    }
  }

  // 处理订单分配
  handleOrderAssigned(data) {
    this.playNotificationSound();

    this.showNotification(
      '新订单分配',
      `订单 ${data.order.order_no} 已分配给您，请及时处理`,
      'warning'
    );

    // 自动刷新任务列表（防抖）
    this.triggerTaskRefresh();

    // 震动提示（如果支持）
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
  }

  // 处理抢单事件
  handleTaskClaimed(data) {
    const userRole = store.getters.userRole;

    if (userRole === 'admin') {
      this.playNotificationSound();

      this.showNotification(
        '任务被抢单',
        `代投员 ${data.claimedBy.username} 抢接了订单 ${data.orderNo}`,
        'success'
      );

      // 刷新订单列表（防抖）
      this.triggerOrderRefresh();
    }
  }

  // 处理批量更新
  handleBatchUpdate(data) {
    const userRole = store.getters.userRole;
    const userId = store.getters.userId;

    // 检查是否影响当前用户
    const affectedUserOrders = data.affectedOrders || [];
    const hasMyOrders = affectedUserOrders.some(order =>
      data.updates.agent_id === userId
    );

    if (hasMyOrders || userRole === 'admin') {
      this.playNotificationSound();

      if (userRole === 'admin') {
        this.showNotification(
          '批量更新',
          `${data.updatedBy} 批量更新了 ${data.count} 个订单`,
          'info'
        );
        this.triggerOrderRefresh();
      } else {
        this.showNotification(
          '订单批量更新',
          `您的 ${affectedUserOrders.length} 个订单已更新`,
          'info'
        );
        this.triggerTaskRefresh();
      }
    }
  }

  // 处理通知
  handleNotification(data) {
    this.showNotification(
      data.title,
      data.content,
      this.getNotificationType(data.type)
    );

    // 更新未读通知数
    store.commit('INCREMENT_UNREAD_NOTIFICATIONS');

    // 触发所有注册的回调
    this.newNotificationCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('通知回调执行失败:', error);
      }
    });
  }

  // 获取通知类型映射
  getNotificationType(type) {
    const typeMap = {
      'order': 'success',
      'assignment': 'warning',
      'system': 'info',
      'message': 'info',
      'alert': 'error'
    };
    return typeMap[type] || 'info';
  }

  // 注册新通知回调
  onNewNotification(callback) {
    if (typeof callback === 'function' && !this.newNotificationCallbacks.includes(callback)) {
      this.newNotificationCallbacks.push(callback);
    }
  }

  // 移除新通知回调
  offNewNotification(callback) {
    const index = this.newNotificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.newNotificationCallbacks.splice(index, 1);
    }
  }

  // 显示通知
  showNotification(title, message, type = 'info') {
    Message({
      message: message,
      title: title,
      type: type,
      duration: NOTIFICATION.DURATION,
      showClose: true
    });

    // 浏览器桌面通知
    if (Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: '/favicon.ico'
      });
    }
  }

  // 播放提示音（可选功能）
  playNotificationSound() {
    // 提示音功能需要真实的音频文件
    // 这里改为使用浏览器通知代替
    if (Notification.permission === 'granted') {
      // 已经授权的通知
    }
    // 静默处理，不显示错误
  }

  // 发送订单更新事件
  emitOrderUpdate(orderId, updates, targetRoles = []) {
    if (this.socket) {
      this.socket.emit('order_updated', {
        orderId,
        updates,
        targetRoles
      });
    }
  }

  // 发送订单分配事件
  emitOrderAssignment(orderId, agentId, assignmentData) {
    if (this.socket) {
      this.socket.emit('order_assigned', {
        orderId,
        agentId,
        assignmentData
      });
    }
  }

  // 发送正在输入状态
  emitTyping(targetUserId, isTyping) {
    if (this.socket) {
      this.socket.emit('typing', {
        targetUserId,
        isTyping
      });
    }
  }

  // 确认通知
  ackNotification(notificationId, senderId) {
    if (this.socket) {
      this.socket.emit('notification_ack', {
        notificationId,
        senderId
      });
    }
  }
}

export default new SocketService();
